import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Globe, RefreshCw, TrendingUp, Award, Puzzle, Map, MessageCircle } from "lucide-react";
import keyVisualCircles from "@/assets/key-visual-circles.svg";
import { Section, SectionHeader } from "./shared";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

const statIconMap: Record<string, typeof Globe> = {
  RefreshCw, TrendingUp, Globe, Award, Puzzle, Map,
};

const COLOR_CLINICAL = "#FFC60C";

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const SolutionWhyQbd = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  if (!solution.whyQbd && !solution.whyQbdSimple) return null;

  const HERO_CARD_SLUGS = ["precision-dx"];
  const isPrecisionDx = HERO_CARD_SLUGS.includes(solution.slug);

  return (
    <>
      {/* Image variant */}
      {solution.whyQbd && (
        <Section bg>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div
              className="rounded-2xl overflow-hidden relative"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={solution.whyQbd.image}
                alt={solution.whyQbd.title}
                className="w-full h-full object-cover rounded-2xl"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0 bg-primary/90 backdrop-blur-sm px-6 py-4 flex items-center justify-around">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-foreground">700+</div>
                  <div className="text-xs text-primary-foreground/70">{t("specialists")}</div>
                </div>
                <div className="w-px h-8 bg-primary-foreground/20" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-foreground">4,500+</div>
                  <div className="text-xs text-primary-foreground/70">{t("projects")}</div>
                </div>
                <div className="w-px h-8 bg-primary-foreground/20" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-foreground">1,200+</div>
                  <div className="text-xs text-primary-foreground/70">{t("clients")}</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{solution.whyQbd.title}</h2>
              <p className="text-primary font-semibold mb-4">{solution.whyQbd.subtitle}</p>
              <p className="text-muted-foreground leading-relaxed mb-6">{solution.whyQbd.content}</p>
              <ul className="space-y-4">
                {solution.whyQbd.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">{point.title}:</span>{" "}
                      <span className="text-muted-foreground text-sm">{point.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <Button className="rounded-full mt-8" size="lg" onClick={onCtaClick}>
                <MessageCircle size={16} className="mr-2" />
                {solution.ctaTitle || t("talkToExpert")}
              </Button>
            </motion.div>
          </div>
        </Section>
      )}

      {/* PrecisionDx — hero card variant */}
      {solution.whyQbdSimple && isPrecisionDx && (
        <section className="py-14 sm:py-20 bg-secondary/30">
          <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
            <motion.div
              className="relative rounded-2xl overflow-hidden bg-primary"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/80 to-primary/95" />

              <div className="relative z-10 px-8 sm:px-12 pt-10 pb-8 sm:pt-14 sm:pb-10">
                <div className="max-w-xl mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR_CLINICAL }} />
                    <p className="text-xs font-semibold text-primary-foreground/50 tracking-widest uppercase">
                      {({ en: "Diagnostics meets drug development", es: "Diagnóstico y fármacos", "zh-cn": "诊断协同药物开发" }[lang])}
                    </p>
                  </div>
                  {solution.sectionLogo && (
                    <img src={solution.sectionLogo} alt="QbD IVD | Qarad" className="h-[120px] sm:h-[148px] w-auto object-contain mt-4 mb-4 brightness-0 invert" />
                  )}
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
                    {solution.whyQbdSimple.subtitle || t("yourTrustedPartner")}
                  </h2>
                  <Button
                    size="lg"
                    className="rounded-full px-8 shadow-lg hover:opacity-90"
                    style={{ backgroundColor: COLOR_CLINICAL, color: '#002855' }}
                    onClick={onCtaClick}
                  >
                    {t("partnerWithUs")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 pt-8 border-t border-primary-foreground/10">
                  {solution.whyQbdSimple.bullets.map((bullet, i) => {
                    const hasBold = bullet.includes("**");
                    const title = hasBold ? bullet.match(/\*\*(.*?)\*\*/)?.[1] || "" : "";
                    const desc = hasBold ? bullet.replace(/\*\*.*?\*\*\s*—?\s*/, "") : bullet;

                    return (
                      <motion.div
                        key={i}
                        className="flex items-start gap-3 p-5 rounded-xl bg-primary-foreground/[0.07] border border-primary-foreground/[0.08] backdrop-blur-sm"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: i * 0.08 }}
                      >
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: COLOR_CLINICAL }} strokeWidth={1.5} />
                        <div>
                          {title && (
                            <p className="text-sm font-semibold text-primary-foreground mb-1.5">{title}</p>
                          )}
                          <p className="text-xs sm:text-sm text-primary-foreground/65 leading-relaxed">{desc}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {solution.whyQbdSimple.stats.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-primary-foreground/10">
                    {solution.whyQbdSimple.stats.map((stat, i) => (
                      <motion.div
                        key={i}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl bg-primary-foreground/[0.07]"
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.08 }}
                      >
                        <span className="text-2xl font-bold" style={{ color: COLOR_CLINICAL }}>{stat.value}</span>
                        <span className="text-xs font-medium text-primary-foreground/70 text-center leading-tight">{stat.label}</span>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Simple bullet variant — dark banner layout */}
      {solution.whyQbdSimple && !isPrecisionDx && (
        <section className="py-14 sm:py-20 bg-secondary/30">
          <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
            <motion.div
              className="relative rounded-2xl overflow-hidden bg-primary"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/80 to-primary/95" />
              <img src={keyVisualCircles} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />

              <div className="relative z-10 px-8 sm:px-12 pt-10 pb-8 sm:pt-14 sm:pb-10">
                <div className="max-w-xl mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-0.5 rounded-full bg-primary-foreground/40" />
                    <p className="text-xs font-semibold text-primary-foreground/50 tracking-widest uppercase">
                      {({ en: "Cross-disciplinary delivery", es: "Colaboración entre disciplinas", "zh-cn": "跨域专长协同交付" }[lang])}
                    </p>
                  </div>
                  {solution.sectionLogo && (
                    <img src={solution.sectionLogo} alt="Partner logo" className="h-[120px] sm:h-[148px] w-auto object-contain mt-4 mb-4 brightness-0 invert" />
                  )}
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
                    {solution.whyQbdSimple.subtitle || solution.whyQbdSimple.title}
                  </h2>
                  <Button
                    size="lg"
                    className="rounded-full px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                    onClick={onCtaClick}
                  >
                    {t("partnerWithUs")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 pt-8 border-t border-primary-foreground/10">
                  {solution.whyQbdSimple.bullets.map((bullet, i) => {
                    const hasBold = bullet.includes("**");
                    const title = hasBold ? bullet.match(/\*\*(.*?)\*\*/)?.[1] || "" : "";
                    const desc = hasBold ? bullet.replace(/\*\*.*?\*\*\s*[—–-]?\s*/, "") : bullet;
                    const fallbackTitle = !hasBold ? bullet.split(/[,.]/)[0] : "";
                    const fallbackDesc = !hasBold ? bullet.slice(fallbackTitle.length).replace(/^[,.\s]+/, "") : "";

                    return (
                      <motion.div
                        key={i}
                        className="flex items-start gap-3 p-5 rounded-xl bg-primary-foreground/[0.07] border border-primary-foreground/[0.08] backdrop-blur-sm"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: i * 0.08 }}
                      >
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5 text-primary-foreground/60" strokeWidth={1.5} />
                        <div>
                          <p className="text-sm font-semibold text-primary-foreground mb-1.5">
                            {title || fallbackTitle}
                          </p>
                          <p className="text-xs sm:text-sm text-primary-foreground/65 leading-relaxed">
                            {hasBold ? desc : fallbackDesc}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {solution.whyQbdSimple.stats.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-primary-foreground/10">
                    {solution.whyQbdSimple.stats.map((stat, i) => {
                      const StatIcon = statIconMap[stat.icon] || Award;
                      return (
                        <motion.div
                          key={i}
                          className="flex flex-col items-center gap-2 p-4 rounded-xl bg-primary-foreground/[0.07]"
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.08 }}
                        >
                          {stat.value ? (
                            <span className="text-2xl font-bold text-primary-foreground">{stat.value}</span>
                          ) : (
                            <StatIcon size={28} className="text-primary-foreground/70" strokeWidth={1.5} />
                          )}
                          <span className="text-xs font-medium text-primary-foreground/70 text-center leading-tight">{stat.label}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </>
  );
};

export default SolutionWhyQbd;
